import type { ModalData } from '@presentation/components';
import type { MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useModal = (params?: Partial<ModalData>) => {
  const [open, setOpen] = useState<boolean>(params?.open ?? false);

  const screenCenterPosition = useMemo((): [number, number] => {
    if (typeof window === 'undefined' || window == null) return [0, 0];
    const x = window.innerWidth / 2;
    const y = window.innerHeight / 2;
    return [x, y];
  }, []);

  const [position, setPosition] = useState<[number, number]>(
    params?.position ?? screenCenterPosition,
  );

  const onCloseClick = useCallback(() => {
    setOpen(false);
    params?.onCloseClick?.();
  }, [params]);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const show = useCallback(
    (fromPosition: [number, number] = screenCenterPosition) => {
      setPosition(fromPosition);
      setOpen(true);
    },
    [screenCenterPosition],
  );

  const showFromClick = useCallback(
    (event: MouseEvent) => {
      const { clientX, clientY } = event;
      show([clientX, clientY]);
    },
    [show],
  );

  useEffect(() => {
    const handleEsc = (event: { keyCode: number }) => {
      if (open && event.keyCode === 27) {
        close();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [close, open]);

  return {
    show,
    showFromClick,
    close,
    modal: {
      open,
      position,
      onCloseClick,
    } as ModalData,
  };
};
